import { EntityTable } from '@/components/table/entity-table.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { EmptyArray, getClassTimesOfSchedule } from '@/lib/api-calls.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { CreateEditClassTime } from '@/routes/_layout/orgs/schools/-components/create-edit-class-time.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { FrequentTimeZones } from '@airreading/common';
import { adjustMinimalClassTimeAcrossTZ, ClassTimeScheduleRow, ClassTimeType } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Edit } from 'lucide-react';
import { useMemo, useState } from 'react';
import { DeleteClassTime } from './delete-class-time.js';
import { CustomTableDialog } from '@/components/ui/custom-table-dialog.js';
import { ClassTimesEligibleCoachesTable } from './class-times-eligible-coaches-table.js';

interface ClassTimesTableProps {
  scheduleId: string;
  classTimeTypes?: ClassTimeType[];
  onEditClassTime?: (classTime: ClassTimeScheduleRow) => void;
  onDeleteClassTime?: (classTime: ClassTimeScheduleRow) => void;
  schoolTimeZone: string;
  hideColumnsIds?: string[];
  afterSave?: () => void;
}

type OpenDialogType = {
  rowId: string;
  type: 'available' | 'conflicts';
};

const columnHelper = createColumnHelper<ClassTimeScheduleRow>();

const renderCoachesCell = (
  coaches: ClassTimeScheduleRow['availableCoaches'],
  rowId: string,
  dialogType: 'available' | 'conflicts',
  openDialog: OpenDialogType | null,
  setOpenDialog: (openDialog: OpenDialogType | null) => void,
) => {
  const isClickable = coaches && coaches.length > 0;
  const isOpenDialog = openDialog?.rowId === rowId && openDialog?.type === dialogType;
  const anchor = (
    <div
      className="cursor-pointer hover:underline"
      onClick={() => (isClickable ? setOpenDialog({ rowId, type: dialogType }) : undefined)}
    >
      {coaches?.length}
    </div>
  );

  return (
    <CustomTableDialog
      open={isOpenDialog}
      setOpen={(open) => setOpenDialog(open ? { rowId, type: dialogType } : null)}
      anchor={anchor}
    >
      <ClassTimesEligibleCoachesTable eligibleCoaches={coaches || []} />
    </CustomTableDialog>
  );
};

const columns = (
  schoolTimeZone: string,
  openDialog: OpenDialogType | null,
  setOpenDialog: (openDialog: OpenDialogType | null) => void,
  afterSave?: () => void,
) => [
  columnHelper.accessor((row) => row, {
    id: 'class-time',
    header: 'Class time (CT)',
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <Badge variant="outline">
          {formatStudentClassTimeAbbr(
            adjustMinimalClassTimeAcrossTZ(classTime, schoolTimeZone, FrequentTimeZones.UsCentral),
          )}
        </Badge>
      );
    },
  }),
  columnHelper.accessor('studentSeats', {
    header: 'Student seats',
    id: 'student-seats',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('studentCount', {
    header: 'Number of students',
    id: 'number-of-students',
    cell: ({ getValue }) => getValue(),
  }),
  columnHelper.accessor('availableCoaches', {
    header: 'Total available teachers',
    id: 'total-available-teachers',
    cell: ({ getValue, row }) => {
      const availableCoaches = getValue();
      return renderCoachesCell(availableCoaches, row.id, 'available', openDialog, setOpenDialog);
    },
  }),
  columnHelper.accessor('availableCoaches', {
    header: 'Conflicts teachers',
    id: 'conflicts-teachers',
    cell: ({ getValue, row }) => {
      const availableCoaches = getValue();
      // Only show coaches that have one time class conflicts
      const conflictsCoaches = availableCoaches?.filter((coach) => coach.conflictType === ClassTimeType.one_time);
      return renderCoachesCell(conflictsCoaches, row.id, 'conflicts', openDialog, setOpenDialog);
    },
  }),
  columnHelper.accessor((row) => row, {
    id: 'actions',
    header: '',
    cell: ({ getValue }) => {
      const classTime = getValue();
      return (
        <div className="flex gap-2 items-center">
          <CreateEditClassTime
            from={{
              fromClassTime: classTime,
              mode: 'edit',
            }}
            trigger={
              <Button size="sm" variant="secondary">
                <Edit className="h-4 w-4" />
              </Button>
            }
            schoolTimeZone={schoolTimeZone}
            modalTitle={`Edit class time`}
            afterSave={afterSave}
          />
          <span className="w-[1px] h-8 bg-slate-400" />
          <DeleteClassTime classTime={classTime} canArchive={classTime.canArchive} afterDelete={afterSave} />
        </div>
      );
    },
  }),
];

export function ClassTimesTable({
  scheduleId,
  classTimeTypes,
  schoolTimeZone,
  hideColumnsIds = [],
  afterSave,
}: ClassTimesTableProps) {
  const { data: classTimes, isPending, refetch, error } = useQuery(getClassTimesOfSchedule(scheduleId));
  useErrorToast(error, 'Error loading class times');

  const filteredClassTimes = useMemo(
    () => (classTimeTypes ? classTimes?.filter((classTime) => classTimeTypes.includes(classTime.type)) : classTimes),
    [classTimes, classTimeTypes],
  );

  const [openDialog, setOpenDialog] = useState<OpenDialogType | null>(null);
  const classTimesTableColumns = useMemo(
    () =>
      columns(schoolTimeZone, openDialog, setOpenDialog, afterSave).filter(
        (column) => column.id && !hideColumnsIds.includes(column.id),
      ),
    [schoolTimeZone, openDialog, hideColumnsIds, afterSave],
  );

  const table = useReactTable({
    data: filteredClassTimes || EmptyArray,
    columns: classTimesTableColumns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <EntityTable
      table={table}
      isLoading={isPending}
      emptyState={'No class times found for this schedule'}
      errorState={<TableErrorState refetchAction={refetch} />}
      hasError={!!error}
      emptyStateClassName="translate-y-0"
      tableKey={`class-times-${scheduleId}`}
      autoManagedColumnIds={['actions']}
    />
  );
}
